import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Mic, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const T = {
  en: { certs: "Reading certificates", rec: "Story recordings", noRec: "No recordings yet. Your child can record a page of a Kiko story or a Level 3 text.",
    page: "page", del: "Delete this recording?", deleted: "Recording deleted", level: "Level", open: "Open / print" },
  fr: { certs: "Certificats de lecture", rec: "Enregistrements des histoires", noRec: "Pas encore d'enregistrement. Votre enfant peut enregistrer une page d'une histoire de Kiko ou un texte du Niveau 3.",
    page: "page", del: "Supprimer cet enregistrement ?", deleted: "Enregistrement supprimé", level: "Niveau", open: "Ouvrir / imprimer" },
};

// Parent: certificates earned + the child's private read-along recordings (play / delete).
export default function ReadingExtrasParent({ childId, lang = "en" }) {
  const t = T[lang] || T.en;
  const [certs, setCerts] = useState([]);
  const [recs, setRecs] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [check, setCheck] = useState([]);
  useEffect(() => {
    api.get(`/reading/${childId}/certificates`).then(({ data }) => setCerts(data.certificates)).catch(() => {});
    api.get(`/reading/${childId}/recordings`).then(({ data }) => setRecs(data)).catch(() => {});
    api.get(`/reading/${childId}/checklist`).then(({ data }) => setCheck(data.items)).catch(() => {});
  }, [childId]);
  const play = async (r) => {
    setPlaying(r.id);
    try {
      const { data } = await api.get(`/reading/recordings/${r.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = new Audio(url);
      a.onended = () => { URL.revokeObjectURL(url); setPlaying(null); };
      await a.play();
    } catch { setPlaying(null); }
  };
  const toggle = async (i) => {
    const next = check.map((c) => (c.i === i ? { ...c, checked: !c.checked } : c));
    setCheck(next);
    await api.post(`/reading/${childId}/checklist`, { checked: next.filter((c) => c.checked).map((c) => c.i) }).catch(() => {});
  };
  const del = async (r) => {
    if (!window.confirm(t.del)) return;
    try {
      await api.delete(`/reading/recordings/${r.id}`);
      setRecs((x) => x.filter((y) => y.id !== r.id));
      toast.success(t.deleted);
    } catch {
      toast.error(lang === "fr" ? "Suppression impossible, réessayez." : "Could not delete, please try again.");
    }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="reading-extras-parent">
      <div className="rounded-xl bg-green-50 p-4 text-sm sm:col-span-2" data-testid="reading-checklist">
        <p className="font-bold">{lang === "fr" ? "Checklist : prêt à lire seul ?" : "Checklist: ready to read alone?"} {check.length > 0 && check.every((c) => c.checked) && <span className="text-green-700">✓ {lang === "fr" ? "Prêt pour les premiers livres !" : "Ready for first books!"}</span>}</p>
        <div className="grid sm:grid-cols-2 gap-1 mt-2">
          {check.map((c) => (
            <label key={c.i} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={c.checked} onChange={() => toggle(c.i)} className="mt-1" data-testid={`reading-checklist-${c.i}`} />
              <span>{c.text}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="rounded-xl bg-amber-50 p-4 text-sm">
        <p className="font-bold flex items-center gap-2"><Award size={16} className="text-amber-600" /> {t.certs}</p>
        {certs.length === 0 ? <p className="text-slate-400 mt-1">—</p> : (
          <ul className="mt-2 space-y-1.5">
            {certs.map((c) => (
              <li key={`${c.lang}-${c.level}`} className="flex items-center justify-between gap-2">
                <span>{c.lang.toUpperCase()} · {t.level} {c.level} : {c.name}</span>
                <Link to={`/reading-certificate/${childId}/${c.lang}/${c.level}`} className="font-bold text-blue-600 underline" data-testid={`parent-cert-${c.lang}-${c.level}`}>{t.open}</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="rounded-xl bg-pink-50 p-4 text-sm">
        <p className="font-bold flex items-center gap-2"><Mic size={16} className="text-pink-600" /> {t.rec}</p>
        {recs.length === 0 ? <p className="text-slate-400 mt-1" data-testid="parent-rec-empty">{t.noRec}</p> : (
          <ul className="mt-2 space-y-2 max-h-64 overflow-auto">
            {recs.map((r) => (
              <li key={r.id} className="flex items-center gap-2" data-testid={`parent-rec-${r.id}`}>
                <button onClick={() => play(r)} disabled={playing === r.id} className="w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center disabled:opacity-60" data-testid={`parent-rec-play-${r.id}`}><Play size={14} /></button>
                <span className="flex-1 min-w-0 truncate">{r.story_title} · {t.page} {r.page + 1} — « {r.page_text} » <span className="text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span></span>
                <button onClick={() => del(r)} className="w-8 h-8 rounded-full hover:bg-pink-100 flex items-center justify-center text-slate-500" data-testid={`parent-rec-delete-${r.id}`}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
